"use client"

import { useState, useCallback, useMemo } from "react"
import { ArrowLeft, ArrowRight } from "lucide-react"
import { useTranslations } from "next-intl"
import { LocalizedLink } from "@/shared/components/i18n/localized-link"
import { track, type EventPropertyMap } from "@/shared/lib/analytics"
import { cn } from "@/shared/lib/utils"

type HeroSlide = {
  eyebrow: string
  title: string
  description: string
  cta: string
  img: string
  img1280: string
  mobileImg: string
  mobileImg768: string
  link: string
  analytics?: EventPropertyMap["landing:hero_cta_click"]
}

export function HeroSlider() {
  const t = useTranslations("landing")
  const [currentSlide, setCurrentSlide] = useState(0)
  const [hasInteracted, setHasInteracted] = useState(false)
  const heroSlides = useMemo<HeroSlide[]>(
    () => [
      {
        eyebrow: t("slides.createCompanion.eyebrow"),
        title: t("slides.createCompanion.title"),
        description: t("slides.createCompanion.description"),
        cta: t("slides.createCompanion.cta"),
        img: "/images/home-hero/pleasurai-hero-create.webp",
        img1280: "/images/home-hero/pleasurai-hero-create-1280.webp",
        mobileImg: "/images/home-hero/pleasurai-hero-create-mobile.webp",
        mobileImg768: "/images/home-hero/pleasurai-hero-create-mobile-768.webp",
        link: "/create",
      },
      {
        eyebrow: t("slides.generateScenes.eyebrow"),
        title: t("slides.generateScenes.title"),
        description: t("slides.generateScenes.description"),
        cta: t("slides.generateScenes.cta"),
        img: "/images/home-hero/pleasurai-hero-generate.webp",
        img1280: "/images/home-hero/pleasurai-hero-generate-1280.webp",
        mobileImg: "/images/home-hero/pleasurai-hero-generate-mobile.webp",
        mobileImg768: "/images/home-hero/pleasurai-hero-generate-mobile-768.webp",
        link: "/generate/image",
      },
      {
        eyebrow: t("slides.talkCharacters.eyebrow"),
        title: t("slides.talkCharacters.title"),
        description: t("slides.talkCharacters.description"),
        cta: t("slides.talkCharacters.cta"),
        img: "/images/home-hero/pleasurai-hero-chat.webp",
        img1280: "/images/home-hero/pleasurai-hero-chat-1280.webp",
        mobileImg: "/images/home-hero/pleasurai-hero-chat-mobile.webp",
        mobileImg768: "/images/home-hero/pleasurai-hero-chat-mobile-768.webp",
        link: "#characters-heading",
        analytics: {
          slide: "talk_characters",
          destination: "characters_grid",
        },
      },
    ],
    [t]
  )
  const slideCount = heroSlides.length
  const safeSlide = slideCount > 0 ? currentSlide % slideCount : 0
  const activeSlide = heroSlides[safeSlide]

  const goToSlide = useCallback((index: number) => {
    if (index === currentSlide) return

    setHasInteracted(true)
    setCurrentSlide(index)
  }, [currentSlide])

  const nextSlide = useCallback(() => {
    setHasInteracted(true)
    setCurrentSlide((prev) => (prev + 1) % slideCount)
  }, [slideCount])

  const prevSlide = useCallback(() => {
    setHasInteracted(true)
    setCurrentSlide((prev) => (prev - 1 + slideCount) % slideCount)
  }, [slideCount])

  return (
    <section className="relative w-full" aria-label={t("sliderLabel")}>
      <div className="mx-auto max-w-7xl px-4 pt-4 sm:px-6 lg:px-8">
        <div className="relative aspect-[2.35/1] overflow-hidden rounded-2xl border border-white/10 bg-card shadow-[0_26px_70px_rgba(0,0,0,0.55)] sm:aspect-[3.4/1] lg:aspect-[2560/516]">
          {activeSlide && (
              <div
                key={safeSlide}
                className={cn(
                  "absolute inset-0",
                  hasInteracted && "animate-in fade-in duration-200"
                )}
              >
                <LocalizedLink
                  href={activeSlide.link}
                  className="group block relative h-full w-full"
                  onClick={() => {
                    if (activeSlide.analytics) {
                      track("landing:hero_cta_click", activeSlide.analytics)
                    }
                  }}
                >
                  <picture className="absolute inset-0 block h-full w-full">
                    <source
                      media="(max-width: 767px)"
                      srcSet={`${activeSlide.mobileImg768} 768w, ${activeSlide.mobileImg} 1280w`}
                      sizes="calc(100vw - 34px)"
                    />
                    {/* The browser selects one art-directed source before fetching,
                        avoiding the desktop-then-mobile double download. */}
                    <img
                      src={activeSlide.img}
                      srcSet={`${activeSlide.img1280} 1280w, ${activeSlide.img} 2560w`}
                      sizes="(max-width: 1280px) calc(100vw - 48px), 1280px"
                      alt={activeSlide.title}
                      className="h-full w-full object-cover object-center transition-transform duration-[5500ms] ease-out group-hover:scale-[1.025]"
                      fetchPriority="high"
                      loading="eager"
                      decoding="async"
                    />
                  </picture>
                  <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/28 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/42 via-transparent to-transparent" />
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_72%_28%,rgba(29,125,242,0.18),transparent_38%)]" />
                  <div className="absolute left-5 top-1/2 z-10 max-w-[min(82%,32rem)] -translate-y-1/2 sm:left-9 lg:left-12">
                    <p className="mb-1.5 text-[9px] font-bold uppercase tracking-[0.26em] text-white/55 sm:text-[10px] lg:text-xs">
                      {activeSlide.eyebrow}
                    </p>
                    <h2 className="max-w-lg text-2xl font-black leading-[1] tracking-normal text-white sm:text-3xl md:text-4xl lg:text-[46px]">
                      {activeSlide.title}
                    </h2>
                    <p className="mt-2 max-w-sm text-xs font-semibold leading-snug text-white/68 sm:text-sm lg:max-w-md lg:text-base">
                      {activeSlide.description}
                    </p>
                    <span className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#1d7df2] px-4 py-2 text-xs font-extrabold text-white shadow-[0_0_34px_rgba(29,125,242,0.48)] transition-transform group-hover:scale-[1.03] sm:mt-4 sm:px-5 sm:py-2.5 sm:text-sm">
                      {activeSlide.cta}
                      <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" />
                    </span>
                  </div>
                </LocalizedLink>
              </div>
          )}

          {slideCount > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-3 top-1/2 z-20 -translate-y-1/2 flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-black/50 text-white/80 backdrop-blur-sm transition-colors hover:bg-black/70 hover:text-white"
                aria-label={t("previousSlide")}
              >
                <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-3 top-1/2 z-20 -translate-y-1/2 flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-black/50 text-white/80 backdrop-blur-sm transition-colors hover:bg-black/70 hover:text-white"
                aria-label={t("nextSlide")}
              >
                <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </>
          )}

          {slideCount > 1 && (
            <div className="absolute bottom-1 left-1/2 z-20 flex -translate-x-1/2 gap-1">
              {heroSlides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goToSlide(i)}
                  className="flex h-6 w-6 items-center justify-center rounded-full"
                  aria-label={t("goToSlide", { number: i + 1 })}
                >
                  <span
                    className={cn(
                      "block h-1.5 rounded-full transition-all duration-300",
                      i === safeSlide ? "w-6 bg-white" : "w-1.5 bg-white/50"
                    )}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
